import React, { useCallback, useState } from "react";
import { Button, Input, Upload, UploadProps, Modal } from "antd";
import { supabase } from "../../../config/supabaseClient";
import {
  addToProductBank,
  extractProductUrl,
} from "../../../Utils/SupabaseServices/ProductBankDatabase";
import { handleProductFileUpload } from "../../../Utils/uploadImage/UploadImage";
import { debounce } from "lodash";
import axios from "axios";

const AddProductModal = ({
  username,
  user_id,
  addProductModalShow,
  setAddProductModalShow,
  refetchData,
  refetchCount,
  linkFilterRefetch,
}: any) => {
  const [productName, setProductName] = useState<string>("");
  const [productLink, setProductLink] = useState<string>("");
  const [productImage, setProductImage] = useState<string>("");
  const [fileList, setFileList] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    // if (setter === setProductLink) {
    //   setErrorMsg("");

    //   try {
    //     // Check if the URL contains the 'u=' parameter
    //     const cleanedUrl = extractProductUrl(value);
    //     setter(cleanedUrl);
    //   } catch (error) {
    //     // If any parsing fails, just continue with the original value
    //   }
    // } else {
      // Update the state with the new value
      // Update the state with the new value
      setter(value);
    // }
  };
  const debouncedUpload = useCallback(
    debounce(async (info) => {
      const { file } = info;

      // Prevent multiple uploads
      if (isUploading) {
        return;
      }

      // Set uploading flag
      setIsUploading(true);

      try {
        await handleProductFileUpload({
          info,
          username,
          productName,
          setFileList,
          setProductImage,
          storageName: "product_bank",
        });
      } finally {
        // Reset uploading flag after completion
        setIsUploading(false);
      }
    }, 300), // Debounce with 300ms delay
    [isUploading, username, productName, supabase]
  );

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: "image/*",
    async onChange(info) {
      setFileList([...info.fileList]);
      debouncedUpload(info);
    },
  };

  const handleCreate = async () => {
    if (!productLink) {
      setErrorMsg("Please enter a product link!");
      return;
    }
    if (!productImage) {
      setErrorMsg("Please upload a product image!");
      return;
    }
    setIsLoading(true);

    try {
      const timestamp = Date.now();
      const table_id = `${username}-${timestamp}`;
      const cleanedUrl = extractProductUrl(productLink);

      // Create optimistic product data
      const optimisticProduct = {
        table_id,
        table_image: productImage,
        table_url: cleanedUrl,
        product_name: productName || "Processing...",
        post_date: new Date().toISOString(),
        platform: "ShopMy",
        is_processing: true,
        table_tags: ""
      };

      // Immediately update UI with optimistic data
      refetchData(optimisticProduct);

      // Close modal and clear form
      setAddProductModalShow(false);
      handleClear();

      // Add to product bank in background
      await addToProductBank(
        table_id,
        productName || "",
        cleanedUrl,
        productImage,
        user_id,
        "",
        username
      );

      // Start OCR processing in background
      const OCRUrl = `https://dev.thesearchbubble.com/embedProductSingle`;
      await axios.post(OCRUrl, {
        user: username,
        from: "api",
        id: table_id,
      });

      // Refresh all data after processing completes
      await Promise.all([
        refetchCount(),
        refetchData(),
        linkFilterRefetch()
      ]);

    } catch (error: any) {
      console.error("Error adding product:", error);
      setErrorMsg(error?.message || "Failed to add product");
      // If there's an error, refresh to get the correct state
      refetchData();
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setProductName("");
    setProductLink("");
    setProductImage("");
    setErrorMsg("");
    setFileList([]);
  };

  const handleCancel = () => {
    setAddProductModalShow(false);
    handleClear();
  };

  const isButtonDisabled =
    isLoading ||
    (productName &&
      productName !== "" &&
      productLink &&
      productLink !== "" &&
      productImage &&
      productImage !== "");

  return (
    <Modal
      open={addProductModalShow}
      onCancel={handleCancel}
      onOk={handleCreate}
      okText="Add"
      data-testid="add-product-modal"
      focusTriggerAfterClose={false}
      getContainer={false}
      okButtonProps={{
        disabled: isLoading,
        loading: isLoading,
      }}
      // okButtonProps={{
      //   disabled: isButtonDisabled || !productName || !productLink || !productImage,
      // }}
    >
      <h5 className="title mb-4 fontsize-16">Add Product</h5>
      <div className="fieldGroup" style={{alignItems: "unset !important"}}>
        <label className="bold-label fontsize-12">Product Name</label>
        <Input
          placeholder="Enter Product Name"
          value={productName}
          onChange={(e: any) => {
            handleInputChange(e.target.value, setProductName);
            setErrorMsg("");
          }}
          className="mb-15"
          id="product-name-add"
        />
        <label className="bold-label fontsize-12">Product Link</label>
        <Input
          placeholder="www.yourlink.com"
          value={productLink}
          onChange={(e: any) => {
            handleInputChange(e.target.value, setProductLink);
            setErrorMsg("");
          }}
          className="mb-15"
          id="product-link-add"
          required
        />

        {/* Move error message here and remove duplicate error message */}
        {errorMsg && (
          <p
            className="errorMsg"
            style={{ color: "red", marginBottom: "15px" }}
          >
            {errorMsg}
          </p>
        )}
        <label className="bold-label fontsize-12">Product Image</label>
        <div className="image-upload-section">
          <Upload
            {...uploadProps}
            multiple={false}
            maxCount={1}
            showUploadList={
              fileList.length === 0 ? false : { showRemoveIcon: false }
            }
          >
            <Button
              className="uploadButton"
              data-testid="upload-product"
              // disabled={!productName}
            >
              <div className="text-center">
                <p className="textContainerHeading">
                  Click to select image or drag & drop to this area to upload
                </p>
                <p className="upload-text"> JPG, JPEG, PNG, HEIC, or WEBP</p>
                <i className="upload-text break-word" style={{ marginTop: '4px', color: '#666' }}>Please upload an image with a resolution of 200px height & 350px width</i>
              </div>
            </Button>
          </Upload>
        </div>

      {/* Preview Section */}
      { productImage ? (
        <>
          <label className="bold-label fontsize-12 mt-2 text-center">Preview</label>
          <div
            className="mt-2 w-100"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <img
              src={productImage}
              alt="Product Preview"
              style={{
                width: "140px",
                height: "250px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #ddd",
              }}
            />
            <p
              style={{
                textAlign: productName !== "" ? "left" : "center",
              }}
            >
              {productName}{" "}
            </p>
          </div>
        </>
      ) : null}
      </div>
    </Modal>
  );
};

export default AddProductModal;
